import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../../shared/api/client";
import { AppBadge, AppButton } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import {
  IconCamera,
  IconCheck,
  IconError,
  IconHourglass,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { useGpsCapture } from "../../field/hooks";
import { OsmMap } from "../../../shared/maps/OsmMap";
import { osmExternalUrl } from "../../../shared/maps/osm";
import { getInstallation, postEvidence, uploadInstallationPhoto } from "../api";
import {
  enqueueInst,
  readPhotoMeta,
  registerInstPhotoFile,
  setPhotoMeta,
  type InstPhotoMeta,
} from "../offline";
import type { ConfirmationMethod, InstallationPhotoType } from "../types";

/**
 * S3 — Evidence capture (handoff TASK-008, s3-*).
 * GPS captured in the browser; the SERVER computes survey_distance_m against
 * the dMRV step-2 survey coordinates — the badge is driven by the server
 * value, never a client-side geofence (limit 3.0 m).
 * Two photos (installed_device, serial_closeup): uploaded immediately when
 * online, queued with an in-memory File when offline (same File-bytes
 * limitation as flow-02 — queue metadata persists, bytes are session-scoped).
 * Customer confirmation (signature | pin) stays DISABLED until GPS is in
 * range; out-of-range offers save-draft (submission is blocked server-side).
 */

const GEOFENCE_LIMIT_M = 3;
const PHOTO_SLOTS: { type: InstallationPhotoType; label: string }[] = [
  { type: "installed_device", label: "Installed device" },
  { type: "serial_closeup", label: "Serial close-up" },
];

type PhotoMetaMap = Partial<Record<InstallationPhotoType, InstPhotoMeta>>;

export function S3EvidenceCapture() {
  const { id = "" } = useParams();
  const installationId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useFieldHeader("Installation evidence", "GPS · photos · customer confirmation");
  useFieldJourney({
    flow: "install",
    step: 2,
    backTo: `/field/installations/${id}/scan`,
  });

  const gps = useGpsCapture();
  const [serverDistance, setServerDistance] = useState<number | null | undefined>(undefined);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [photoMeta, setPhotoMetaState] = useState<PhotoMetaMap>(() => readPhotoMeta(installationId));
  const [method, setMethod] = useState<ConfirmationMethod>("signature");
  const postedGpsRef = useRef<string | null>(null);

  const query = useQuery({
    queryKey: ["installation", installationId],
    queryFn: () => getInstallation(installationId),
    retry: 1,
    staleTime: 10_000,
  });

  const inst = query.data?.installation;

  // ── GPS → server evidence (once per capture) ───────────────────────────────
  const gpsMutation = useMutation({
    mutationFn: (body: { gps_lat: number; gps_lng: number; gps_accuracy_m?: number }) =>
      postEvidence(installationId, body),
    onSuccess: (res) => {
      setServerDistance(res.installation.survey_distance_m);
      setGpsError(null);
      void queryClient.invalidateQueries({ queryKey: ["installation", installationId] });
    },
    onError: (err) => {
      setGpsError(err instanceof Error ? err.message : "Could not save GPS evidence");
    },
  });

  useEffect(() => {
    if (gps.state.phase !== "captured") return;
    const { lat, lng, accuracyM } = gps.state;
    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (postedGpsRef.current === key) return;
    postedGpsRef.current = key;
    gpsMutation.mutate({
      gps_lat: lat,
      gps_lng: lng,
      ...(accuracyM !== null ? { gps_accuracy_m: accuracyM } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gps.state]);

  // ── Photos ─────────────────────────────────────────────────────────────────
  const applyMeta = (type: InstallationPhotoType, meta: InstPhotoMeta) => {
    setPhotoMeta(installationId, type, meta);
    setPhotoMetaState((prev) => ({ ...prev, [type]: meta }));
  };

  const handlePhoto = async (type: InstallationPhotoType, file: File) => {
    if (!navigator.onLine) {
      registerInstPhotoFile(installationId, type, file);
      applyMeta(type, { state: "pending", name: file.name });
      enqueueInst({
        queueId: crypto.randomUUID(),
        kind: "photo-upload",
        installationId,
        photoType: type,
        createdAt: new Date().toISOString(),
      });
      return;
    }
    applyMeta(type, { state: "uploading", name: file.name });
    registerInstPhotoFile(installationId, type, file);
    try {
      const res = await uploadInstallationPhoto(installationId, type, file);
      applyMeta(type, { state: "uploaded", url: res.photo.photo_url, name: file.name });
      void queryClient.invalidateQueries({ queryKey: ["installation", installationId] });
    } catch {
      applyMeta(type, { state: "failed", name: file.name });
    }
  };

  const retryPhoto = (type: InstallationPhotoType) => {
    const input = document.getElementById(`inst-photo-${type}`) as HTMLInputElement | null;
    input?.click();
  };

  // ── Customer confirmation ──────────────────────────────────────────────────
  const confirmMutation = useMutation({
    mutationFn: () =>
      postEvidence(installationId, { customer_confirmed: true, confirmation_method: method }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["installation", installationId] }),
  });

  // Server distance wins; fall back to the persisted value on the installation.
  const distance: number | null =
    serverDistance !== undefined ? serverDistance : (inst?.survey_distance_m ?? null);
  const hasServerGps =
    gps.state.phase === "captured" || (inst?.gps_latitude != null && inst?.gps_longitude != null);
  const inRange = distance !== null && distance <= GEOFENCE_LIMIT_M;
  const outOfRange = distance !== null && distance > GEOFENCE_LIMIT_M;

  // Merge server photo records into the local metadata map.
  const mergedPhotoMeta: PhotoMetaMap = useMemo(() => {
    const merged: PhotoMetaMap = { ...photoMeta };
    for (const p of inst?.photos ?? []) {
      if (merged[p.photo_type]?.state !== "uploaded" || !merged[p.photo_type]?.url) {
        merged[p.photo_type] = { state: "uploaded", url: p.photo_url };
      }
    }
    return merged;
  }, [photoMeta, inst?.photos]);

  // ── Loading / error ────────────────────────────────────────────────────────
  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Loading task…</h2>
      </div>
    );
  }

  if (query.isError || !inst) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Could not load task</h2>
        <p>{query.error instanceof Error ? query.error.message : "Something went wrong"}</p>
        <AppButton variant="secondary" type="button" onClick={() => void query.refetch()}>
          Retry
        </AppButton>
      </div>
    );
  }

  const confirmBlocked = !inRange;
  const confirmErr =
    confirmMutation.error instanceof ApiError ? confirmMutation.error : null;

  return (
    <>
      {/* ── GPS vs survey ─────────────────────────────────────────────────── */}
      <div
        className={`pg-gps-widget${outOfRange ? " out-of-range" : ""}${
          gps.state.phase === "error" ? " error" : ""
        }`}
      >
        <div>
          <div className="gps-label">Installation GPS</div>
          {hasServerGps ? (
            <>
              <div className="gps-coords">
                {gps.state.phase === "captured"
                  ? `${gps.state.lat.toFixed(5)}, ${gps.state.lng.toFixed(5)}`
                  : `${inst.gps_latitude?.toFixed(5)}, ${inst.gps_longitude?.toFixed(5)}`}
              </div>
              <div className="gps-meta">
                {gps.state.phase === "captured" && gps.state.accuracyM !== null
                  ? `±${gps.state.accuracyM} m · `
                  : ""}
                {distance !== null
                  ? `${distance.toFixed(1)} m from survey point`
                  : "distance pending"}
              </div>
            </>
          ) : (
            <div className="gps-meta">
              {gps.state.phase === "acquiring"
                ? "Acquiring GPS…"
                : gps.state.phase === "error"
                  ? gps.state.message
                  : "Stand at the installed device and capture GPS"}
            </div>
          )}
        </div>
        {hasServerGps ? (
          inRange ? (
            <AppBadge tone="success">
              <IconCheck size={12} /> In range
            </AppBadge>
          ) : outOfRange ? (
            <AppBadge tone="warning">
              <IconWarning size={12} /> Out of range
            </AppBadge>
          ) : (
            <AppBadge tone="neutral">Captured</AppBadge>
          )
        ) : gps.state.phase === "acquiring" ? (
          <IconSpinner size={18} />
        ) : (
          <AppButton variant="secondary" type="button" onClick={gps.capture}>
            Capture GPS
          </AppButton>
        )}
      </div>

      {hasServerGps && (gps.state.phase === "captured" || inst.gps_latitude != null) ? (
        <AppButton variant="secondary" type="button" onClick={gps.capture} disabled={gps.state.phase === "acquiring"}>
          Re-capture GPS
        </AppButton>
      ) : null}

      {(() => {
        const lat =
          gps.state.phase === "captured" ? gps.state.lat : inst?.gps_latitude ?? null;
        const lng =
          gps.state.phase === "captured" ? gps.state.lng : inst?.gps_longitude ?? null;
        const acc = gps.state.phase === "captured" ? gps.state.accuracyM : null;
        if (lat == null || lng == null) return null;
        return (
          <div style={{ marginTop: 12 }}>
            <OsmMap
              pins={[{ lat, lng, popup: "Installation GPS", accuracyM: acc }]}
              height={200}
              zoom={17}
              center={[lat, lng]}
            />
            <p className="pg-text-muted" style={{ marginTop: 8, fontSize: "0.8rem" }}>
              <a href={osmExternalUrl(lat, lng)} target="_blank" rel="noopener noreferrer">
                Open in OpenStreetMap ↗
              </a>
            </p>
          </div>
        );
      })()}

      {inst.survey_lat != null && inst.survey_lng != null ? (
        <div className="pg-summary">
          <div className="row">
            <span className="k">Site survey point</span>
            <span className="v">
              {inst.survey_lat.toFixed(5)}, {inst.survey_lng.toFixed(5)}
            </span>
          </div>
          <div className="row">
            <span className="k">Server distance</span>
            <span className="v">
              {distance !== null ? `${distance.toFixed(1)} m (limit ${GEOFENCE_LIMIT_M} m)` : "—"}
            </span>
          </div>
        </div>
      ) : null}

      {gpsError ? (
        <div className="pg-inline-error" role="alert">
          <IconError size={14} /> {gpsError}
        </div>
      ) : null}

      {outOfRange ? (
        <div className="pg-geofence-warning" role="alert">
          <IconWarning size={14} />
          GPS is {distance !== null ? distance.toFixed(1) : "—"} m from the survey point (limit{" "}
          {GEOFENCE_LIMIT_M} m). Customer confirmation and submission are blocked — re-capture at
          the installed device or save a draft.
        </div>
      ) : null}

      {/* ── Photos ────────────────────────────────────────────────────────── */}
      <div className="pg-photo-row">
        {PHOTO_SLOTS.map(({ type, label }) => {
          const meta = mergedPhotoMeta[type];
          const state = meta?.state;
          const cls =
            state === "uploaded"
              ? " captured"
              : state === "uploading"
                ? " uploading"
                : state === "failed"
                  ? " failed"
                  : "";
          return (
            <label key={type} className={`pg-photo-tile${cls}`}>
              <input
                id={`inst-photo-${type}`}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handlePhoto(type, file);
                  e.target.value = "";
                }}
              />
              {state === "uploading" ? <IconSpinner size={20} /> : <IconCamera size={20} />}
              {label}
              <span className="tile-state">
                {state === "uploaded"
                  ? "Uploaded ✓"
                  : state === "uploading"
                    ? "Uploading…"
                    : state === "pending"
                      ? "Pending sync"
                      : state === "failed"
                        ? "Failed — tap to retry"
                        : "Tap to capture"}
              </span>
              {state === "failed" ? (
                <button
                  type="button"
                  className="pg-linklike"
                  onClick={(e) => {
                    e.preventDefault();
                    retryPhoto(type);
                  }}
                >
                  Retry
                </button>
              ) : null}
            </label>
          );
        })}
      </div>

      {/* ── Customer confirmation ─────────────────────────────────────────── */}
      {inst.customer_confirmed ? (
        <div className="pg-summary">
          <div className="row">
            <span className="k">Customer confirmation</span>
            <span className="v">
              <AppBadge tone="success">
                <IconCheck size={12} />
                {inst.confirmation_method === "pin" ? "PIN" : "Signature"} confirmed
              </AppBadge>
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="pg-option-list" role="group" aria-label="Confirmation method">
            <button
              type="button"
              className={`pg-option${method === "signature" ? " selected" : ""}`}
              aria-pressed={method === "signature"}
              onClick={() => setMethod("signature")}
              disabled={confirmBlocked}
            >
              {method === "signature" ? <IconCheck size={14} /> : null}
              Customer signs on device
            </button>
            <button
              type="button"
              className={`pg-option${method === "pin" ? " selected" : ""}`}
              aria-pressed={method === "pin"}
              onClick={() => setMethod("pin")}
              disabled={confirmBlocked}
            >
              {method === "pin" ? <IconCheck size={14} /> : null}
              Customer enters PIN
            </button>
          </div>

          {confirmBlocked ? (
            <div className="pg-geofence-warning" role="status">
              <IconHourglass size={14} />
              Customer confirmation is blocked until GPS is in range of the survey point.
            </div>
          ) : null}

          {confirmMutation.isError ? (
            <div className="pg-inline-error" role="alert">
              <IconError size={14} />
              {confirmErr?.message ?? "Confirmation failed — retry."}
            </div>
          ) : null}

          <AppButton
            variant="secondary"
            type="button"
            onClick={() => confirmMutation.mutate()}
            disabled={confirmBlocked || confirmMutation.isPending}
          >
            {confirmMutation.isPending ? <IconSpinner size={14} /> : null}
            Record customer confirmation
          </AppButton>
        </>
      )}

      {/* ── Bottom actions ────────────────────────────────────────────────── */}
      <div className="pg-actions">
        {outOfRange ? (
          <AppButton
            variant="secondary"
            type="button"
            onClick={() => navigate("/field/installations")}
          >
            Save draft (submission blocked)
          </AppButton>
        ) : (
          <AppButton
            type="button"
            onClick={() => navigate(`/field/installations/${installationId}/payment`)}
          >
            Continue to payment check
          </AppButton>
        )}
      </div>
    </>
  );
}
